import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { styled } from '@mui/material/styles';
import { beehiveTheme } from '../../styles/beehiveTheme';
import wrootLogo from '../../assets/wroot-logo.png';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: beehiveTheme.neutral.white,
  boxShadow: beehiveTheme.shadows.md,
}));

const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  cursor: 'pointer',
  gap: theme.spacing(1),
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto',
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: beehiveTheme.primary.main,
  marginLeft: theme.spacing(2),
  fontWeight: 500,
  '&:hover': {
    backgroundColor: beehiveTheme.neutral.offWhite,
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  backgroundColor: beehiveTheme.accent.main,
  color: beehiveTheme.neutral.white,
  fontWeight: 600,
  '&:hover': {
    backgroundColor: beehiveTheme.accent.dark,
  },
}));

export default function BeehiveNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileOpen(false);
  };

  const navItems = [
    { label: 'Problem', id: 'problem' },
    { label: 'Solution', id: 'solution' },
    { label: 'How It Works', id: 'how-it-works' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <LogoImage src={wrootLogo} alt="Wroot Logo" />
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.id} button onClick={() => scrollToSection(item.id)}>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        <ListItem button onClick={() => scrollToSection('contact')}>
          <ListItemText primary="Contact Us" sx={{ color: beehiveTheme.accent.main, fontWeight: 600 }} />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <StyledAppBar position="fixed">
        <Toolbar>
          <Logo onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <LogoImage src={wrootLogo} alt="Wroot Logo" />
          </Logo>
          
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: beehiveTheme.primary.main }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {navItems.map((item) => (
                <NavButton key={item.id} onClick={() => scrollToSection(item.id)}>
                  {item.label}
                </NavButton>
              ))}
              <CTAButton variant="contained" onClick={() => scrollToSection('contact')}>
                Contact Us
              </CTAButton>
            </Box>
          )}
        </Toolbar>
      </StyledAppBar>
      
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
      
      {/* Spacer to prevent content from going under fixed navbar */}
      <Toolbar />
    </>
  );
}
